import { endpoint } from 'services/endpoint';
import { httpPay } from 'services/http';
import { TPaymentGatewayApi, TPgReceiptFile } from './types';

const ep = endpoint.paymentGateway;

const DEFAULT_RECEIPT_FILE_NAME = 'receipt.pdf';

/** File name from `Content-Disposition` (`filename*=UTF-8''…` wins over `filename=`). */
export const fileNameFromContentDisposition = (
	header: string | undefined | null,
	fallback: string = DEFAULT_RECEIPT_FILE_NAME,
): string => {
	if (!header) return fallback;
	const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)?''([^;]+)/.exec(header);
	if (extended?.[1]) {
		try {
			return decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ''));
		} catch {
			/* fall through to the plain parameter */
		}
	}
	const plain = /filename\s*=\s*("?)([^";]+)\1/.exec(header);
	return plain?.[2]?.trim() || fallback;
};

const legalEntityParams = (legalEntityId?: number | null) =>
	legalEntityId ? { params: { legalEntityId } } : undefined;

export const paymentGateway: TPaymentGatewayApi = {
	// Operator and system
	getMe: () => httpPay.get(ep.ME).then((response) => response.data),
	getSystem: () => httpPay.get(ep.SYSTEM).then((response) => response.data),
	getInvoiceReceipts: (invoiceIds) =>
		httpPay.get(ep.INVOICE_RECEIPTS(invoiceIds)).then((response) => response.data),

	// 4.1 Services and mappings
	getServices: () => httpPay.get(ep.SERVICES).then((response) => response.data),
	createService: (body) => httpPay.post(ep.SERVICES, body).then((response) => response.data),
	updateService: ({ id, body }) =>
		httpPay.put(ep.SERVICE(id), body).then((response) => response.data),
	getServiceTypeMappings: () =>
		httpPay.get(ep.SERVICE_TYPE_MAPPINGS).then((response) => response.data),
	upsertServiceTypeMapping: ({ serviceType, body }) =>
		httpPay.put(ep.SERVICE_TYPE_MAPPING(serviceType), body).then((response) => response.data),
	deleteServiceTypeMapping: (serviceType) =>
		httpPay.delete(ep.SERVICE_TYPE_MAPPING(serviceType)).then(() => undefined),

	// 4.2 Debt transfer rules
	getPayabilityRules: () => httpPay.get(ep.PAYABILITY_RULES).then((response) => response.data),
	createPayabilityRule: (body) =>
		httpPay.post(ep.PAYABILITY_RULES, body).then((response) => response.data),
	updatePayabilityRule: ({ id, body }) =>
		httpPay.put(ep.PAYABILITY_RULE(id), body).then((response) => response.data),
	deletePayabilityRule: (id) => httpPay.delete(ep.PAYABILITY_RULE(id)).then(() => undefined),
	evaluatePayability: (body) =>
		httpPay.post(ep.PAYABILITY_RULES_EVALUATE, body).then((response) => response.data),

	// 4.3 Payment links and invoices
	createPaymentLink: (body) =>
		httpPay.post(ep.PAYMENT_LINKS, body).then((response) => response.data),
	getInvoice: (invoiceId) => httpPay.get(ep.INVOICE(invoiceId)).then((response) => response.data),

	// 4.4 Legal entities and Airwallex accounts
	getLegalEntities: () => httpPay.get(ep.LEGAL_ENTITIES).then((response) => response.data),
	createLegalEntity: (body) =>
		httpPay.post(ep.LEGAL_ENTITIES, body).then((response) => response.data),
	updateLegalEntity: ({ id, body }) =>
		httpPay.put(ep.LEGAL_ENTITY(id), body).then((response) => response.data),
	getAirwallexAccounts: () => httpPay.get(ep.AIRWALLEX_ACCOUNTS).then((response) => response.data),
	updateAirwallexAccount: ({ id, body }) =>
		httpPay.put(ep.AIRWALLEX_ACCOUNT(id), body).then((response) => response.data),
	syncAirwallexAccountWebhooks: (id) =>
		httpPay.post(ep.AIRWALLEX_ACCOUNT_WEBHOOKS_SYNC(id), {}).then((response) => response.data),
	syncAirwallexAccountApplePayDomains: ({ id, body }) =>
		httpPay
			.post(ep.AIRWALLEX_ACCOUNT_APPLE_PAY_DOMAINS_SYNC(id), body)
			.then((response) => response.data),
	checkAirwallexAccountCredentials: (id) =>
		httpPay.post(ep.AIRWALLEX_ACCOUNT_CREDENTIALS_CHECK(id), {}).then((response) => response.data),

	// 4.5 Fees and DD FX
	getFees: (legalEntityId) =>
		httpPay.get(ep.FEES, legalEntityParams(legalEntityId)).then((response) => response.data),
	updateFees: (body) => httpPay.put(ep.FEES, body).then((response) => response.data),
	previewFee: (body) => httpPay.post(ep.FEES_PREVIEW, body).then((response) => response.data),
	getFxSettings: (legalEntityId) =>
		httpPay.get(ep.FX_SETTINGS, legalEntityParams(legalEntityId)).then((response) => response.data),
	updateFxSettings: ({ legalEntityId, body }) =>
		httpPay.put(ep.FX_SETTINGS_UPDATE(legalEntityId), body).then((response) => response.data),
	previewFx: (body) => httpPay.post(ep.FX_SETTINGS_PREVIEW, body).then((response) => response.data),

	// 4.6 Schedule and runs
	getSchedule: () => httpPay.get(ep.SCHEDULE).then((response) => response.data),
	updateSchedule: (body) => httpPay.put(ep.SCHEDULE, body).then((response) => response.data),
	getRecurringRuns: (body) =>
		httpPay.post(ep.RECURRING_RUNS_LIST, body).then((response) => response.data),
	getRecurringRun: (id) => httpPay.get(ep.RECURRING_RUN(id)).then((response) => response.data),
	startRecurringRun: (body) =>
		httpPay.post(ep.RECURRING_RUNS, body).then((response) => response.data),
	getRecurringCycles: (body) =>
		httpPay.post(ep.RECURRING_CYCLES_LIST, body).then((response) => response.data),

	// Customer access, EAS updates pause and customer e-mails
	getClientAccess: () => httpPay.get(ep.CLIENT_ACCESS).then((response) => response.data),
	updateClientAccess: (body) =>
		httpPay.put(ep.CLIENT_ACCESS, body).then((response) => response.data),

	// 4.7 Customers and payment methods
	getCustomers: (body) => httpPay.post(ep.CUSTOMERS_LIST, body).then((response) => response.data),
	getCustomer: (id) => httpPay.get(ep.CUSTOMER(id)).then((response) => response.data),
	getCustomerByClientNumber: (clientNumber) =>
		httpPay.get(ep.CUSTOMER_BY_CLIENT_NUMBER(clientNumber)).then((response) => response.data),
	updateCustomerUkOverride: ({ id, body }) =>
		httpPay.put(ep.CUSTOMER_UK_OVERRIDE(id), body).then((response) => response.data),
	refreshCustomer: (id) =>
		httpPay.post(ep.CUSTOMER_REFRESH(id), {}).then((response) => response.data),
	syncCustomerConsents: (id) =>
		httpPay.post(ep.CUSTOMER_SYNC_CONSENTS(id), {}).then((response) => response.data),
	getCustomerLegacyTransactions: ({ customerId, body }) =>
		httpPay
			.post(ep.CUSTOMER_LEGACY_TRANSACTIONS_LIST(customerId), body)
			.then((response) => response.data),
	disablePaymentMethod: ({ id, body }) =>
		httpPay.post(ep.PAYMENT_METHOD_DISABLE(id), body).then((response) => response.data),
	updatePaymentMethodServices: ({ id, body }) =>
		httpPay.put(ep.PAYMENT_METHOD_SERVICES(id), body).then((response) => response.data),

	// 4.8 Payments
	getPayments: (body) => httpPay.post(ep.PAYMENTS_LIST, body).then((response) => response.data),
	getPayment: (transactionId) =>
		httpPay.get(ep.PAYMENT(transactionId)).then((response) => response.data),
	syncPayment: (transactionId) =>
		httpPay.post(ep.PAYMENT_SYNC(transactionId), {}).then((response) => response.data),
	easSyncPayment: ({ transactionId, body }) =>
		httpPay.post(ep.PAYMENT_EAS_SYNC(transactionId), body).then((response) => response.data),
	chargeInvoice: (body) =>
		httpPay.post(ep.PAYMENTS_CHARGE_INVOICE, body).then((response) => response.data),
	chargeCustomer: (body) =>
		httpPay.post(ep.PAYMENTS_CHARGE_CUSTOMER, body).then((response) => response.data),

	// 4.9 Refunds
	createRefund: ({ transactionId, body }) =>
		httpPay.post(ep.PAYMENT_REFUNDS(transactionId), body).then((response) => response.data),
	getRefunds: (body) => httpPay.post(ep.REFUNDS_LIST, body).then((response) => response.data),
	getRefund: (id) => httpPay.get(ep.REFUND(id)).then((response) => response.data),
	syncRefund: (id) => httpPay.post(ep.REFUND_SYNC(id), {}).then((response) => response.data),

	// 4.10 FX conversions
	getFxConversions: (body) =>
		httpPay.post(ep.FX_CONVERSIONS_LIST, body).then((response) => response.data),
	getFxConversion: (id) => httpPay.get(ep.FX_CONVERSION(id)).then((response) => response.data),
	syncFxConversion: (id) =>
		httpPay.post(ep.FX_CONVERSION_SYNC(id), {}).then((response) => response.data),
	retryFxConversion: ({ id, body }) =>
		httpPay.post(ep.FX_CONVERSION_RETRY(id), body).then((response) => response.data),
	approveFxConversion: ({ id, body }) =>
		httpPay.post(ep.FX_CONVERSION_APPROVE(id), body).then((response) => response.data),
	convertPayment: (transactionId) =>
		httpPay.post(ep.PAYMENT_CONVERT(transactionId), {}).then((response) => response.data),

	// 4.11 Notifications and receipts
	getNotifications: (body) =>
		httpPay.post(ep.NOTIFICATIONS_LIST, body).then((response) => response.data),
	resendNotification: (id) =>
		httpPay
			.post(ep.NOTIFICATION_RESEND(id), {})
			.then((response) => (response.status === 204 ? null : response.data)),
	downloadReceipt: (transactionId) =>
		httpPay
			.get<Blob>(ep.PAYMENT_RECEIPT(transactionId), {
				responseType: 'blob',
				headers: { Accept: 'application/pdf' },
			})
			.then(
				(response): TPgReceiptFile => ({
					blob: response.data,
					fileName: fileNameFromContentDisposition(
						response.headers?.['content-disposition'] as string | undefined,
						`receipt-${transactionId}.pdf`,
					),
				}),
			),
	sendReceipt: ({ transactionId, body }) =>
		httpPay
			.post(ep.PAYMENT_RECEIPT_SEND(transactionId), body)
			.then((response) => (response.status === 204 ? null : response.data)),
	getEmailTypes: () => httpPay.get(ep.NOTIFICATIONS_EMAIL_TYPES).then((response) => response.data),
	runRelinkCampaign: (body) =>
		httpPay.post(ep.NOTIFICATIONS_RELINK_CAMPAIGN, body).then((response) => response.data),

	// 4.12 Alerts, outbox, audit
	getAlerts: (body) => httpPay.post(ep.ALERTS_LIST, body).then((response) => response.data),
	getAlert: (id) => httpPay.get(ep.ALERT(id)).then((response) => response.data),
	acknowledgeAlert: (id) =>
		httpPay.post(ep.ALERT_ACKNOWLEDGE(id), {}).then((response) => response.data),
	resolveAlert: ({ id, body }) =>
		httpPay.post(ep.ALERT_RESOLVE(id), body).then((response) => response.data),
	rollbackEasForAlert: ({ id, body }) =>
		httpPay.post(ep.ALERT_ROLLBACK_EAS(id), body).then((response) => response.data),
	reapplyEasForAlert: ({ id, body }) =>
		httpPay.post(ep.ALERT_REAPPLY_EAS(id), body).then((response) => response.data),
	getOutboxMessages: (body) => httpPay.post(ep.OUTBOX_LIST, body).then((response) => response.data),
	getOutboxMessage: (id) => httpPay.get(ep.OUTBOX_MESSAGE(id)).then((response) => response.data),
	retryOutboxMessage: ({ id, body }) =>
		httpPay.post(ep.OUTBOX_RETRY(id), body).then((response) => response.data),
	getAudit: (body) => httpPay.post(ep.AUDIT_LIST, body).then((response) => response.data),

	// 4.13 Webhooks and reconciliation
	getWebhookEvents: (body) =>
		httpPay.post(ep.WEBHOOK_EVENTS_LIST, body).then((response) => response.data),
	getWebhookEvent: (id) => httpPay.get(ep.WEBHOOK_EVENT(id)).then((response) => response.data),
	replayWebhookEvent: (id) =>
		httpPay.post(ep.WEBHOOK_EVENT_REPLAY(id), {}).then((response) => response.data),
	runReconciliation: (body) =>
		httpPay.post(ep.RECONCILIATION_RUN, body).then((response) => response.data),
	getLastReconciliation: (scope) =>
		httpPay
			.get(ep.RECONCILIATION_LAST, scope ? { params: { scope } } : undefined)
			.then((response) => response.data),
};
